import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ContentService } from '@blk/cwp-ng-framework';
import { DzModalRef } from '@blk/daisy/modules/modals';
import { ReportSection } from '@blk/simba';
import { ReportData, ReportGenerationService } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, PDFDataErrorDCR, ViewCategory } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, ComponentData } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { ClientOutput, OutputFlag, SharedApplicationConstants } from '@ddt-web/domain';
import { ErrorPopupService, PopupDialogAction, PopupDialogActionOptions, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { MarketViewConfigService } from '@ddt-web/market-view';
import { StaticContentSection } from '@ddt-web/pdf-components';
import { PdfGenerateStoreConfig } from '@ddt-web/pdf-generate';
import { AchoReportComposerService, AchoReportConfiguration, ReportUtils } from '@ddt-web/pdf-utils';
import { ApplicationState, ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { Config, StoreService } from '@ddt-web/store-service';
import { PartnerQueryParamKey, UrlQueryParamService, UrlRedirectService, UrlUtils } from '@ddt-web/url';
import { UserDataService } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as find from 'lodash/find';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, tap, timeout } from 'rxjs/operators';

import { ModelsBrowseStoreConfig } from '../config';
import { ModelsBrowseDataService } from './models-browse-data.service';
import { ModelsBrowseReportConfigService } from './models-browse-report-config.service';

@UntilDestroy()
@Injectable({
	providedIn: 'root'
})
export class ModelsBrowseAllPdfReportsDownloadService {
	public families: any;
	public familyIds: string[] = [];
	public testPartnerFirm: string;
	public isBlkModel: boolean = false;
	public isEsgFamily: boolean = false;
	public modelExternalName: string;
	public model: ClientOutput;
	public includeScenarioYield: boolean = false;
	public includeYield: boolean = false;
	public showDollarAmountAsDefault: boolean = false;
	public advisorLogo: string = ' ';
	public includeBenchmark: boolean = false;
	public disableAdvisorLogo: boolean = true;
	public componentDataMapping$: Observable<Map<string, ComponentData>>;
	public reportPages: Array<string> = null;
	public name: string;
	public firm: string;
	public description: string;
	public isLoading: boolean = true;
	public reportType: string;
	public showScenarioYield: boolean = false;
	public portfolioStrategist: string = '';
	public engagementUniqueId: string = '';
	public achoReportConfiguration: AchoReportConfiguration;
	public reportCover: string;
	public excludePerformance: boolean = false;

	public readonly failedMessage = PopupDialogObject.create({
		success: false,
		title: this.contentService.get('FAIL_DIALOG_HEADING', 'Failed!'),
		description: this.contentService.get('PDF_DATA_RETRIEVAL_ERROR', PDFDataErrorDCR.PDF_DATA_RETRIEVAL_ERROR),
		actions: [
			PopupDialogAction.create({
				description: 'Try refresh page',
				label: 'Refresh',
				reload: true
			} as PopupDialogActionOptions)
		]
	} as PopupDialogOptions);

	public outputFlags: OutputFlag[] = [
		new OutputFlag('includeRisk', true),
		new OutputFlag('includeYield', true),
		new OutputFlag('showDollarAmountAsDefault', true),
		new OutputFlag('includeScenarioYield', true),
		new OutputFlag('includeTaxes', true),
		new OutputFlag('includeEtfComp', true),
		new OutputFlag('includeFees', true),
		new OutputFlag('red', true),
		new OutputFlag('yellow', true),
		new OutputFlag('green', true)
	];

	@InjectConfig(ModelsExperienceConfigKey.SHOW_PDF_MODULARITY) private readonly showPdfModularity: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_ESG_PDF_PAGES) private readonly showEsgPdfPages: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_CMS_PDF_MODULARITY) private readonly showCmsPdfModularity: boolean;

	constructor(
		private dialogService: DialogService,
		private storeService: StoreService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private activatedRoute: ActivatedRoute,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private marketViewConfigService: MarketViewConfigService,
		private urlRedirectService: UrlRedirectService,
		private reportGenerationService: ReportGenerationService,
		private contentService: ContentService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig,
		private errorPopupService: ErrorPopupService,
		private reportComposer: AchoReportComposerService,
		private userDataService: UserDataService,
		private modelBrowse: ModelsBrowseReportConfigService,
		private urlQueryParamService: UrlQueryParamService
	) { }

	public downloadAllPdfReport(families: any, categories: string[], familyModelsPerCategory: { [categoryId: string]: { [familyId: string]: string[] } }, excludePerformance: boolean = false): void {
		this.families = families;
		this.familyIds = families.ids;
		this.excludePerformance = excludePerformance;
		this.dialogService.showLoading();
		const cmsFamilyIds = [];
		categories.forEach(category => {
			if (category !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP && familyModelsPerCategory[category]) {
				cmsFamilyIds.push(...Object.keys(familyModelsPerCategory[category]));
			}
		});
		this.testPartnerFirm = this.urlQueryParamService?.queryParams?.[PartnerQueryParamKey.TEST_PARTNER_FIRM];
		this.familyIds = this.testPartnerFirm ? cmsFamilyIds : this.familyIds;
		this.familyIds.forEach(familyId => {
			combineLatest([
				this.modelsBrowseDataService.fetchFamilyPortfolioAnalysis(familyId),
				this.storeService.selectAll(ModelsBrowseStoreConfig.familyPerformance),
				this.modelsBrowseDataService.fetchFamilyModelsAnalytics(familyId),
				this.applicationStateStoreProxyService.getPerformanceState(),
				this.modelsBrowseDataService.getFamilyNameId(familyId)
			])
				.pipe(
					first(([modelId, familyPerformanceStoreConfig, familyId, performanceState, familyNameId]) => !!modelId && !familyPerformanceStoreConfig.isLoading),
					switchMap(([modelId, familyPerformanceStoreConfig, familyId, performanceState, familyNameId]) => {
						const api$: Observable<any>[] = [
							this.modelsBrowseDataService.checkPerformanceDataValidity(familyId, performanceState?.customAdvisoryFee),
							this.marketViewConfigService.fetchConfig(familyNameId)
						];
						if (this.isEsgFamily && this.showEsgPdfPages) {
							api$.push(this.modelsBrowseDataService.fetchESGModelsAnalytics(familyId));
						}
						return combineLatest(api$);
					}),
					first()
				)
				.subscribe(([isPerformanceDataValid]) => {
					this.storeService.upsertOne(ModelsBrowseStoreConfig.reportConfig, {
						reportType: ViewCategory.FA_SELF_SERVICE_PDF,
						enableModularity: this.showPdfModularity && (this.isBlkModel || this.showCmsPdfModularity),
						isPerformanceDataValid: isPerformanceDataValid,
						yieldVisibility: { hideYield: true },
						isBlkModel: this.isBlkModel
					});
					this.generateReport(familyId);
				});
		});
	}

	private generateReport(familyId: string): void {
		const modelFamiliesObj = find(this.families.entities, ['id', familyId]);
		const groupId = this.testPartnerFirm ? this.testPartnerFirm + '_group' : SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP;
		const familyName = modelFamiliesObj?.description ?? '';
		this.modelExternalName = groupId !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP ? modelFamiliesObj?.modelExternalName ?? '' : null;
		this.applicationStateStoreProxyService.updateGenerateReportData({ additionalHeaderInfo: !isEmpty(this.modelExternalName) ? this.modelExternalName : familyName });
		combineLatest([this.getReportType$(), this.getUserDetails$()])
			.pipe(untilDestroyed(this))
			.subscribe(([reportType, profile]) => {
				if (!reportType) {
					if (isEmpty(this.activatedRoute.snapshot.queryParams)) {
						this.urlRedirectService.redirectToRoute('/');
					} else {
						this.redirectToTopOfMainPage();
					}
				} else {
					this.reportType = reportType;
					const defaultFirmName = profile?.firmName ?? '';
					this.name = this.model?.clientName ?? profile?.fullName ?? '';
					this.firm = this.model?.firm ?? defaultFirmName;
					this.description = this.model?.advisorName ?? '';
					this.portfolioStrategist = this.model?.portfolioStrategist ?? '';
					this.engagementUniqueId = this.model?.uniqueID ?? '';
					this.reset();
					this.reportCover = this.getReport(this.model, familyId, groupId);
					this.achoReportConfiguration = this.modelBrowse.getSelfServiceConfig(this.model.outputFlags, new Map(), [], familyId, groupId, this.excludePerformance);
				}
			});

		this.componentDataMapping$ = this.reportComposer
			.getComponentDataMapping(this.achoReportConfiguration, this.contentService, null, this.achoReportConfiguration?.componentReqIdMapping)
			.pipe(
				filter(mapping => this.areAllComponentDataAvailable(mapping)),
				timeout(SharedApplicationConstants.REPORT_GENERATION_REQUEST_TIMEOUT),
				first()
			);

		this.componentDataMapping$.pipe(take(1)).subscribe(() => (this.isLoading = false));

		this.model.outputFlags = [
			new OutputFlag('includeScenarioYield', this.includeScenarioYield),
			new OutputFlag('includeYield', this.includeYield),
			new OutputFlag('showDollarAmountAsDefault', this.showDollarAmountAsDefault),
			new OutputFlag('includeBenchmark', this.includeBenchmark)
		];

		this.model['advisorLogo'] = this.advisorLogo;
		const loadingTitle = this.contentService.get('LOADING_TEXT', 'Loading...');
		const loadingDialog = this.errorPopupService.openResponseMessageDialog(PopupDialogObject.create({ title: loadingTitle, showSpinner: true } as PopupDialogOptions));
		this.applicationStateStoreProxyService
			.getApplicationState()
			.pipe(first())
			.subscribe(appState => this.composeReportByConfig(loadingDialog, appState, familyId, this.modelExternalName || familyName, groupId));
		this.storeService.upsertOne(this.storeConfig.reportConfig || ({ name: 'reportConfig' } as Config<any>), {
			includeYield: false,
			includeScenarioYield: false,
			showDollarAmountAsDefault: false
		});
	}

	private areAllComponentDataAvailable(mapping: Map<string, ComponentData>): boolean {
		for (const componentData of Array.from(mapping.values())) {
			if (componentData.isLoading) {
				return false;
			}
		}
		return true;
	}

	private reset(): void {
		this.model = new ClientOutput(this.name, this.description, this.firm, this.outputFlags, this.portfolioStrategist, this.engagementUniqueId);
	}

	private getReport(clientOutput: ClientOutput, familyId: string, groupId?: string): string {
		const reportCoverPage = this.getReportPage(clientOutput.outputFlags, 0, familyId, groupId) as StaticContentSection;
		const reportCoverPageSvg = reportCoverPage.getResult();
		const reportCover = ReportUtils.parseSvgElement(reportCoverPageSvg);
		return reportCover;
	}

	private getReportPage(outputFlags: OutputFlag[], pageIndex: number, familyId: string, groupId?: string): ReportSection {
		const reportConfig = this.modelBrowse.getSelfServiceConfig(outputFlags, new Map(), [], familyId, groupId, this.excludePerformance);
		const reportPageElement = reportConfig.pages[pageIndex];
		return reportPageElement;
	}

	private getReportType$(): Observable<string> {
		return this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).pipe(
			map((data: any) => data?.reportType ?? null),
			untilDestroyed(this)
		);
	}

	private getUserDetails$(): Observable<any> {
		return this.userDataService.getUserProfile().pipe(untilDestroyed(this));
	}

	private overrideModel(): ClientOutput {
		const override = { advisorLogo: ' ', coverPageFirm: this.firm };
		return Object.assign({}, this.model, override);
	}

	private constructFileName(familyName: string): string {
		return familyName + '.pdf';
	}

	private redirectToTopOfMainPage(): void {
		this.urlRedirectService.redirectToMainPage('models-experience').then((navSuccess: boolean) => {
			if (navSuccess) {
				UrlUtils.resetScroll();
			}
		});
	}

	private composeReportByConfig(loadingDialog: DzModalRef, applicationState: ApplicationState, familyId: string, familyName: string, groupId?: string): void {
		const reportConfig = this.modelBrowse.getSelfServiceConfig(this.model.outputFlags, new Map(), [], familyId, groupId, this.excludePerformance);
		const model = this.overrideModel();
		this.reportComposer
			.composeReport(reportConfig, this.contentService, model, applicationState, this.componentDataMapping$)
			.pipe(
				untilDestroyed(this),
				switchMap(pages => {
					const report = new ReportData();
					report.tracking = new Map();
					report.svgPages = pages;
					report.fileName = this.constructFileName(familyName);

					const reportGeneration$ = this.reportGenerationService.generateReport(report, true, false).pipe(first());
					return reportGeneration$.pipe(
						tap(done => {
							loadingDialog.close();
							if (done) {
								this.redirectToTopOfMainPage();
							}
						}),
						catchError(err => {
							this.handlePdfError(err);
							throw err;
						})
					);
				}),
				catchError(err => {
					this.handlePdfError(err);
					throw err;
				})
			)
			.subscribe();
	}

	private handlePdfError(err): void {
		console.error('Error occurred', err);
		this.failedMessage.requestId = err?.headers?.get('x-request-id');
		this.errorPopupService.closeAllDialogs();
		this.errorPopupService.openResponseMessageDialog(this.failedMessage, true);
	}
}
